import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardCheck, ListTodo, UserRoundCheck } from "lucide-react";
import { MetricCard } from "@/components/common/MetricCard";
import type { UsersKpiSummary } from "@/types/users-kpis";

interface UsersKpiPanelProps {
  summary?: UsersKpiSummary;
  isLoading?: boolean;
}

export const UsersKpiPanel: React.FC<UsersKpiPanelProps> = ({
  summary,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const percentFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        maximumFractionDigits: 2,
      }),
    [i18n.language],
  );

  const loadingValue = isLoading ? "…" : "—";
  const busiestEmployee = summary?.busiestEmployee ?? null;
  let busiestEmployeeDelta: string | undefined;

  if (busiestEmployee) {
    busiestEmployeeDelta = t("users.kpi.activeTasksCount", {
      count: busiestEmployee.activeTasksCount,
      defaultValue: "{{count}} active tasks",
    });
  } else if (summary) {
    busiestEmployeeDelta = t("users.kpi.noActiveTasks", "No active tasks");
  }

  return (
    <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <MetricCard
        title={t("users.kpi.tasksInProgress", "Number of tasks in progress")}
        value={summary ? String(summary.tasksInProgress) : loadingValue}
        icon={<ListTodo />}
        width="fill"
      />
      <MetricCard
        title={t("users.kpi.completedOnTime", "Tasks completed on time")}
        value={
          summary
            ? `${percentFormatter.format(summary.tasksCompletedOnTimePercent)}%`
            : loadingValue
        }
        delta={
          summary
            ? t("users.kpi.completedTasksCount", {
                count: summary.completedTasksCount,
                defaultValue: "{{count}} completed tasks",
              })
            : undefined
        }
        icon={<ClipboardCheck />}
        width="fill"
      />
      <MetricCard
        title={t("users.kpi.busiestEmployee", "The busiest employee")}
        value={busiestEmployee?.name ?? loadingValue}
        valueClassName="truncate text-[22px]"
        delta={busiestEmployeeDelta}
        icon={<UserRoundCheck />}
        width="fill"
      />
    </section>
  );
};
